import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

export function CountrySelector({
  countries,
  selectedCountries,
  completedExtractions,
  onToggle,
  onSelectAll,
  onSelectPending,
  onClear,
  onProceedSingle,
  onProceedMulti,
}) {
  const isExtracted = (name) =>
    completedExtractions.some((c) => c.country === name && !c.first_level);

  const getBeachCount = (name) => {
    const matches = completedExtractions.filter((c) => c.country === name);
    return matches.reduce((sum, c) => sum + (c.beaches_count || 0), 0);
  };

  const selectedCount = selectedCountries.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs text-slate-400">2. Countries ({countries.length} available)</label>
        <div className="flex gap-2">
          <Button data-testid="btn-select-pending-countries" variant="outline" size="sm" onClick={onSelectPending} className="border-slate-700 text-[10px] h-6 px-2">Select Pending</Button>
          <Button data-testid="btn-select-all-countries" variant="outline" size="sm" onClick={onSelectAll} className="border-slate-700 text-[10px] h-6 px-2">Select All</Button>
        </div>
      </div>
      <ScrollArea className="h-52">
        <div className="grid grid-cols-2 gap-1.5">
          {countries.map((c) => {
            const name = c.name;
            const extracted = isExtracted(name);
            const beachCount = getBeachCount(name);
            const selected = selectedCountries.some((sc) => sc.name === name);

            let cardClass = "bg-slate-800/50 hover:bg-slate-700/50 border border-transparent hover:border-slate-600";
            if (selected) {
              cardClass = "bg-cyan-500/20 border border-cyan-500/50";
            } else if (extracted) {
              cardClass = "bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/50";
            }

            return (
              <div
                key={`country-${name}`}
                data-testid={`country-item-${name.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => onToggle(c)}
                className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all text-sm ${cardClass}`}
              >
                <Checkbox checked={selected} className="pointer-events-none shrink-0 h-3.5 w-3.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    {extracted && (
                      <svg className="w-3 h-3 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span className={`truncate ${extracted ? "text-emerald-300" : ""}`}>{name}</span>
                  </div>
                  {extracted && beachCount > 0 && (
                    <div className="text-[9px] text-emerald-500/70">{beachCount.toLocaleString()} beaches</div>
                  )}
                </div>
                {c.has_subregions && <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0">sub</Badge>}
              </div>
            );
          })}
        </div>
      </ScrollArea>
      {selectedCount > 0 && (
        <div className="flex items-center justify-between border-t border-slate-800 pt-3">
          <div className="text-sm text-slate-400">
            {selectedCount} of {countries.length} selected
          </div>
          <div className="flex gap-2">
            <Button data-testid="btn-clear-countries" variant="outline" size="sm" onClick={onClear} className="border-slate-700">Clear</Button>
            {selectedCount === 1 ? (
              <Button data-testid="btn-proceed-single-country" onClick={() => onProceedSingle(selectedCountries[0])} className="bg-cyan-600 hover:bg-cyan-500">
                Analyze {selectedCountries[0].name}
              </Button>
            ) : (
              <Button data-testid="btn-proceed-multi-country" onClick={onProceedMulti} className="bg-emerald-600 hover:bg-emerald-500">
                Extract {selectedCount} Countries
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
